/**
 * Utility functions for URL handling and normalization
 */

/**
 * Normalizes a backend URL by:
 * - Trimming whitespace
 * - Removing trailing slashes
 * - Removing extra spaces
 *
 * @param url - The URL to normalize
 * @returns The normalized URL string
 */
export function normalizeBackendUrl(url: string): string {
  if (!url) {
    throw new Error('URL cannot be empty');
  }
  return url.trim().replace(/\/+$/, '').replace(/\s+/g, '');
}

/** Joins browser-facing apiEndpoint with a relative path from the API (e.g. FileMapper image/download URLs). */
export function joinApiEndpointPath(apiEndpoint: string | undefined | null, dtoPath: string | undefined): string {
  if (!dtoPath) {
    return '';
  }
  if (!apiEndpoint) {
    return dtoPath;
  }
  const base = normalizeBackendUrl(apiEndpoint);
  const path = dtoPath.startsWith('/') ? dtoPath : `/${dtoPath}`;
  return `${base}${path}`;
}

/**
 * Browser URL for a link the API returns either as an absolute URL (kept as is) or as a path relative to the API
 * base (joined with `apiEndpoint`, see {@link joinApiEndpointPath}). Undefined when the API sent nothing.
 */
export function resolveApiLinkUrl(
  apiEndpoint: string | undefined | null,
  url: string | undefined | null
): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) {
    return undefined;
  }
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : joinApiEndpointPath(apiEndpoint, trimmed);
}

/**
 * Creates a complete backend URL by combining base URL, relative path, and query parameters
 *
 * @param baseUrl - The base URL of the backend
 * @param relativeUrl - The relative path to append
 * @param params - Optional query parameters
 * @returns A complete URL object
 * @throws Error if URL creation fails
 */
export function createBackendUrl(
  baseUrl: string,
  relativeUrl: string,
  params: Record<string, string | undefined | null> = {}
): URL {
  if (!baseUrl) {
    throw new Error('Backend URL not initialized');
  }

  const normalizedBaseUrl = normalizeBackendUrl(baseUrl);
  // Ensure relativeUrl doesn't start with a slash to avoid double slashes
  const normalizedRelativeUrl = relativeUrl.startsWith('/') ? relativeUrl.slice(1) : relativeUrl;

  // Filter out undefined/null params
  const nonEmptyParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      nonEmptyParams[key] = value;
    }
  }

  try {
    const url = new URL(
      normalizedRelativeUrl,
      normalizedBaseUrl.endsWith('/') ? normalizedBaseUrl : normalizedBaseUrl + '/'
    );
    url.search = new URLSearchParams(nonEmptyParams).toString();
    return url;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(
      `Failed to create backend URL: ${errorMessage}. ` +
        `Base URL: ${normalizedBaseUrl}, Relative URL: ${normalizedRelativeUrl}`
    );
  }
}
